/**
 * @license
 * Copyright (c) 2008 %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { getCmdPoints } from '#utils/PRIVATE_chartUtils/utils';
import { AllLineType } from '#utils/UNSAFE_visTypes/common/common.types';
import { fixCoord, Point } from './lineUtils';
import { curveThroughPoints } from './pathUtils';

/**
 * Returns a straight area path command.
 * @param {array} lineSegmentCoords Polyline top points array.
 * @param {array} bottomCoords Polyline bottom points array.
 * @return {string} Straight area path commands.
 */
export function getNonCurveAreaPath(
  lineSegmentCoords: Point[],
  bottomCoords: Point[],
  areaType: AllLineType,
  isHoriz: boolean,
  isRtl: boolean,
  groupWidth?: number,
  timeAxisType?: 'enabled' | 'skipGaps' | 'mixedFrequency'
) {
  // Create the path data string
  const highCoords = getCmdPoints(
    lineSegmentCoords,
    areaType,
    isHoriz,
    isRtl,
    groupWidth,
    timeAxisType,
    true
  ).split(',');
  const lowCoords = getCmdPoints(
    bottomCoords,
    areaType,
    isHoriz,
    isRtl,
    groupWidth,
    timeAxisType,
    true
  ).split(',');

  // Reverse the lowArray
  const revLowArray = [];
  for (let i = 0; i < lowCoords.length; i += 2) {
    revLowArray.unshift(lowCoords[i], lowCoords[i + 1]);
  }
  return revLowArray.concat(highCoords);
}

/**
 * Returns a curved area path command, based on cubic hermite splines, that goes through the points in the points array.
 * @param {array} lineSegmentCoords Polyline top points array.
 * @param {array} bottomCoords Polyline bottom points array.
 * @param {boolean} connectWithLine Whether the first point is reached using lineTo. Otherwise, moveTo is used.
 * @param {string} splineType The spline type.
 * @return {string} Curved area path commands.
 */
export function getCurveAreaPath(
  lineSegmentCoords: Point[],
  bottomCoords: Point[],
  connectWithLine: boolean,
  splineType: string,
  isBottomSegmentCurved?: boolean
) {
  let pathData;
  if (lineSegmentCoords.length > 0) {
    if (!isBottomSegmentCurved) {
      pathData =
        curveThroughPoints(lineSegmentCoords, connectWithLine, splineType) +
        [...bottomCoords]
          .reverse()
          .map((bottomCoord) => {
            return `L ${bottomCoord[0]} ${bottomCoord[1]}`;
          })
          .join(' ') +
        'Z';
    } else {
      pathData =
        curveThroughPoints(lineSegmentCoords, false, splineType) +
        ' ' +
        curveThroughPoints([...bottomCoords].reverse(), true, splineType) +
        ' Z';
    }
  }
  return pathData;
}

export function getCmdsForArea(
  lineSegmentCoords: Point[],
  bottomCoords: Point[],
  areaType: AllLineType,
  isHoriz: boolean,
  isBottomSegmentCurved: boolean,
  isRtl: boolean,
  groupWidth?: number,
  timeAxisType?: 'enabled' | 'skipGaps' | 'mixedFrequency'
) {
  const l = lineSegmentCoords.map((coord) => fixCoord(coord, isHoriz));
  const b = bottomCoords.map((coord) => fixCoord(coord, isHoriz));
  if (areaType === 'curved') {
    return getCurveAreaPath(l, b, false, isHoriz ? 'mh' : 'mv', isBottomSegmentCurved);
  } else {
    return getNonCurveAreaPath(
      lineSegmentCoords,
      bottomCoords,
      areaType,
      isHoriz,
      isRtl,
      groupWidth,
      timeAxisType
    ).join(' ');
  }
}
